import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Button, Card, Chip, KpiCard, Tag, useToast } from '@kollez/design-system';
import { formatINR } from '@kollez/shared';
import { useWorkflow, usePublishWorkflow, useRetireWorkflow, useWorkflowRunsSummary, type WorkflowStageLite, type WorkflowRunsSummary } from '@/lib/queries';

/**
 * /workflows/:id — read-only detail page for a Workflow template (v1.6 §7.6).
 *
 * Shows the stage graph as an ordered list. For each stage: author hat, reversibility class,
 * allowedNext list, monetization hinge, satisfiedByBadge gate, emitsBadge config, on-revoke
 * policy. Every field is a placement-operating-system invariant; a TPO or partner reads this
 * page to decide whether to reuse the template or fork it (Step 24 authoring).
 */
export function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useWorkflow(id);
  const w = q.data?.workflow;
  const publish = usePublishWorkflow(id);
  const retire = useRetireWorkflow(id);
  const toast = useToast();

  async function onPublish() {
    try {
      await publish.mutateAsync();
      toast.show({ message: 'Workflow published.' });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Publish failed.' });
    }
  }
  async function onRetire() {
    try {
      await retire.mutateAsync();
      toast.show({ message: 'Workflow retired.' });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Retire failed.' });
    }
  }

  return (
    <AsyncBoundary
      kind="detail"
      loading={q.isLoading}
      error={q.error}
      empty={!q.isLoading && !w}
      emptyTitle="Workflow not found"
      emptyHint="It may have been retired, or you may not have visibility into this tenant's templates."
    >
      {w ? (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">
                <Link className="link" to="/workflows">← All workflows</Link>
                {' · '}
                v{w.version} · type {w.type}
              </p>
              <h1 className="h-page">{w.name}</h1>
              <p className="subhead">
                {w.description ?? 'No description provided.'}
              </p>
            </div>
            <div className="ph-actions">
              <Chip tone={w.status === 'live' ? 'success' : w.status === 'draft' ? 'warning' : 'default'}>
                {w.status}
              </Chip>
              {w.status === 'draft' ? (
                <Button variant="primary" onClick={onPublish} loading={publish.isPending}>Publish</Button>
              ) : null}
              {w.status === 'live' ? (
                <Button variant="ghost" onClick={onRetire} loading={retire.isPending}>Retire</Button>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <KpiCard label="Stages" value={w.stages.length} />
            <KpiCard label="Scope" value={w.scope} />
            <KpiCard label="Type" value={w.type} />
            <KpiCard label="Status" value={w.status} />
          </div>

          <Card padding="lg">
            <h2 className="h-section mb-3">Stage graph</h2>
            <ol className="grid gap-2">
              {[...w.stages].sort((a, b) => a.order - b.order).map((s) => (
                <StageRow key={s.key} stage={s} />
              ))}
            </ol>
          </Card>

          <RunsSummarySection workflowId={id} />
        </div>
      ) : null}
    </AsyncBoundary>
  );
}

function fmtDwell(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  const days = Math.floor(sec / 86400);
  if (days >= 1) return `${days}d`;
  const hrs = Math.floor(sec / 3600);
  if (hrs >= 1) return `${hrs}h`;
  const min = Math.max(1, Math.floor(sec / 60));
  return `${min}m`;
}

function RunsSummarySection({ workflowId }: { workflowId: string | undefined }) {
  const q = useWorkflowRunsSummary(workflowId);
  const data: WorkflowRunsSummary | undefined = q.data;

  if (q.isLoading) return null;
  if (!data || data.totals.runs === 0) return null;

  const stageEntries = Object.entries(data.stages).sort((a, b) => b[1] - a[1]);
  const maxStage = Math.max(1, ...stageEntries.map(([, n]) => n));

  const totalRevenue = Object.values(data.revenue).reduce((sum, r) => sum + (r.amountInr ?? 0), 0);
  const paidRevenue = (data.revenue['paid']?.amountInr ?? 0);

  return (
    <Card padding="lg">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h2 className="h-section m-0">Runs</h2>
        <Link to="/workflows/runs" className="link">All workflow runs →</Link>
      </div>

      <div className="grid gap-3 md:grid-cols-3 mb-4">
        <KpiCard label="Total runs" value={data.totals.runs} />
        <KpiCard
          label="Revenue linked"
          value={totalRevenue > 0 ? formatINR(totalRevenue) : '—'}
          meta={paidRevenue > 0 ? `${formatINR(paidRevenue)} paid` : undefined}
        />
        <KpiCard
          label="Distinct tenants"
          value={data.topTenants.length}
        />
      </div>

      {stageEntries.length > 0 && (
        <div className="mb-4">
          <div className="cell-sub mb-1">Where runs sit right now</div>
          <ol className="stack">
            {stageEntries.map(([stage, count]) => {
              const dwell = data.dwell[stage];
              return (
                <li key={stage} className="grid grid-cols-[minmax(0,1fr)_60px_50px_36px] items-center gap-2">
                  <span className="truncate"><Tag>{stage}</Tag></span>
                  <span className="bar"><span style={{ width: `${Math.max(6, Math.round((count / maxStage) * 100))}%` }} /></span>
                  <span className="text-right text-sm cell-sub">
                    {dwell ? `~${fmtDwell(dwell.avgSec)}` : '—'}
                  </span>
                  <span className="text-right text-sm">{count}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {data.topTenants.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Top tenants</div>
          <ol className="stack">
            {data.topTenants.map((t) => (
              <li key={t.tenantId} className="flex items-center gap-2">
                <span>{t.name ?? t.slug ?? t.tenantId}</span>
                <span className="ml-auto text-sm">{t.count} runs</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}

function StageRow({ stage }: { stage: WorkflowStageLite }) {
  return (
    <li className="cell" style={{ padding: '12px 14px' }}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <b>{stage.order}. {stage.label}</b>
        <Tag pressed>{stage.key}</Tag>
        <Chip tone="default">{stage.author}</Chip>
        <Chip tone={
          stage.reversibilityClass === 'irreversible' ? 'danger'
          : stage.reversibilityClass === 'reversible_with_cost' ? 'warning'
          : 'success'
        }>
          {stage.reversibilityClass}
        </Chip>
      </div>
      <div className="cell-sub mb-2">
        Next: {stage.allowedNext.length === 0 ? <em>terminal</em> : stage.allowedNext.join(' · ')}
      </div>

      {stage.monetization ? (
        <div className="cell-sub" style={{ marginBottom: 4 }}>
          <b>Monetization:</b> {stage.monetization.model}
          {stage.monetization.chargeOn ? ` · charge on ${stage.monetization.chargeOn}` : ''}
          {stage.monetization.fee?.amountInr ? ` · ${formatINR(stage.monetization.fee.amountInr)}` : ''}
        </div>
      ) : null}
      {stage.satisfiedByBadge ? (
        <div className="cell-sub">
          <b>Auto-satisfied by badge:</b> {stage.satisfiedByBadge.skill} (min level {stage.satisfiedByBadge.minLevel})
        </div>
      ) : null}
      {stage.emitsBadge ? (
        <div className="cell-sub">
          <b>Emits badge:</b> {stage.emitsBadge.skill} ({stage.emitsBadge.taxonomy})
        </div>
      ) : null}
    </li>
  );
}
